import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon, Menu } from 'semantic-ui-react';
import { usePopup } from '../../lib/popup';

import NotificationsStep from './NotificationsStep';
import HamburgerStep from '../HamburgerStep';
// FAMILY-EXT: переключатель тёмной темы
import {
  isFamilyDarkTheme,
  toggleFamilyDarkTheme,
} from '../../utils/family-theme';

import styles from './Header.module.scss';

const POPUP_PROPS = {
  position: 'bottom right',
};

// FAMILY-EXT: компактные иконки тулбара доски — рядом с именем проекта.
// collapsible=true — иконка скрывается на десктопе (там содержимое
// уезжает в inline-режим прямо в шапку).
// FAMILY-EXT: иконка досок убрана — её роль теперь играет клик по имени
// проекта (открывает picker проект+доска). Остались только участники
// и фильтры, оба collapsible (на десктопе уезжают в inline-режим).
const BOARD_TABS = [
  { key: 'members', icon: 'users', title: 'Участники', collapsible: true },
  { key: 'filters', icon: 'filter', title: 'Фильтры', collapsible: true },
];

const Header = React.memo(
  ({
    project,
    user,
    notifications,
    isLogouting,
    canEditProject,
    canEditUsers,
    onProjectSettingsClick,
    onUsersClick,
    onNotificationDelete,
    onUserSettingsClick,
    onLogout,
    // FAMILY-EXT: внешние хранилища
    onStoragesClick,
    // FAMILY-EXT: интеграция тулбара досок (опциональные пропсы — если
    // их нет, шапка как раньше)
    boardTab,
    onBoardTabClick,
    currentBoardName,
    canEditBoard,
    onBoardSettingsClick,
    // FAMILY-EXT: на десктопе сюда приходит JSX с inline-вариантом
    // фильтров и участников (BoardActionsContainer mode=... inline);
    // на мобиле — скрывается через media-query.
    inlineActions,
  }) => {
    // FAMILY-EXT: переключатель тёмной темы
    const [isDark, setIsDark] = useState(isFamilyDarkTheme());
    const handleThemeToggleClick = useCallback(() => {
      setIsDark(toggleFamilyDarkTheme());
    }, []);

    const NotificationsPopup = usePopup(NotificationsStep, POPUP_PROPS);
    const HamburgerPopup = usePopup(HamburgerStep, POPUP_PROPS);

    return (
      <div className={styles.wrapper}>
        <Menu inverted size="large" className={styles.menu}>
          {project && (
            <Menu.Menu position="left">
              {/* FAMILY-EXT: стрелка назад убрана — переход к выбору проекта
                  теперь через клик по имени проекта (open picker). */}
              {/* FAMILY-EXT: имя проекта и доски — кликабельный блок,
                  открывает picker проект+доска ниже шапки. */}
              <Menu.Item
                className={classNames(
                  styles.item,
                  styles.itemHoverable,
                  styles.projectBlock,
                  boardTab === 'picker' && styles.projectBlockActive,
                )}
                onClick={onBoardTabClick ? () => onBoardTabClick('picker') : undefined}
              >
                <div className={styles.projectName}>{project.name}</div>
                {currentBoardName && (
                  <div className={styles.boardName}>{currentBoardName}</div>
                )}
              </Menu.Item>
              {/* FAMILY-EXT: иконки тулбара доски справа от блока.
                  members/filters — collapsible: иконка только на мобиле,
                  на десктопе их содержимое показывается inline ниже. */}
              {onBoardTabClick && BOARD_TABS.map((tab) => (
                <Menu.Item
                  key={tab.key}
                  className={classNames(
                    styles.item,
                    styles.itemHoverable,
                    styles.boardTabIcon,
                    tab.collapsible && styles.boardTabIconCollapsible,
                    boardTab === tab.key && styles.boardTabIconActive,
                  )}
                  onClick={() => onBoardTabClick(tab.key)}
                  title={tab.title}
                >
                  <Icon fitted name={tab.icon} />
                </Menu.Item>
              ))}
              {/* FAMILY-EXT: inline-фильтры и участники на десктопе.
                  Разделитель — отдельный span с фикс. высотой, чтобы
                  не растягивался на всю 50px высоту панели и не уезжал
                  ниже (поведение зависит от высоты Menu.Item). */}
              {inlineActions && (
                <Menu.Item className={classNames(styles.item, styles.inlineActions)}>
                  <span className={styles.inlineActionsDivider} />
                  {inlineActions}
                </Menu.Item>
              )}
            </Menu.Menu>
          )}
          <Menu.Menu position="right">
            <NotificationsPopup items={notifications} onDelete={onNotificationDelete}>
              <Menu.Item className={classNames(styles.item, styles.itemHoverable)}>
                <Icon fitted name="bell" />
                {notifications.length > 0 && (
                  <span className={styles.notification}>{notifications.length}</span>
                )}
              </Menu.Item>
            </NotificationsPopup>
            {/* FAMILY-EXT: гамбургер-меню — настройки, юзеры, тема, выход */}
            <HamburgerPopup
              isLogouting={isLogouting}
              isDark={isDark}
              canEditUsers={canEditUsers}
              canEditProject={canEditProject}
              canEditBoard={canEditBoard}
              onSettingsClick={onUserSettingsClick}
              onUsersClick={onUsersClick}
              onProjectSettingsClick={onProjectSettingsClick}
              onBoardSettingsClick={onBoardSettingsClick}
              onStoragesClick={onStoragesClick}
              onThemeToggle={handleThemeToggleClick}
              onLogout={onLogout}
            >
              <Menu.Item className={classNames(styles.item, styles.itemHoverable)}>
                <Icon fitted name="bars" />
              </Menu.Item>
            </HamburgerPopup>
          </Menu.Menu>
        </Menu>
      </div>
    );
  },
);

Header.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  project: PropTypes.object,
  user: PropTypes.object.isRequired,
  notifications: PropTypes.array.isRequired,
  /* eslint-enable react/forbid-prop-types */
  isLogouting: PropTypes.bool.isRequired,
  canEditProject: PropTypes.bool.isRequired,
  canEditUsers: PropTypes.bool.isRequired,
  onProjectSettingsClick: PropTypes.func.isRequired,
  onUsersClick: PropTypes.func.isRequired,
  onNotificationDelete: PropTypes.func.isRequired,
  onUserSettingsClick: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  // FAMILY-EXT
  boardTab: PropTypes.string,
  onBoardTabClick: PropTypes.func,
  currentBoardName: PropTypes.string,
  canEditBoard: PropTypes.bool,
  onBoardSettingsClick: PropTypes.func,
  // FAMILY-EXT
  onStoragesClick: PropTypes.func,
  inlineActions: PropTypes.node,
};

Header.defaultProps = {
  project: undefined,
  // FAMILY-EXT
  boardTab: null,
  onBoardTabClick: undefined,
  currentBoardName: undefined,
  canEditBoard: false,
  onBoardSettingsClick: undefined,
  onStoragesClick: () => {},
  inlineActions: undefined,
};

export default Header;
